/**
 * VesselMind Learning Service
 * 
 * Optional learning mode that stores anonymized Q&A pairs locally
 * to improve retrieval ranking and context selection over time.
 * 
 * IMPORTANT:
 * - Completely isolated to VesselMind sandbox
 * - Only stores data in browser's localStorage
 * - Never alters PDF documents
 * - Only active when explicitly enabled by user
 * - Fully stateless when disabled
 */

const LEARNING_MODE_KEY = 'vesselmind_learning_mode_enabled';
const LEARNING_DATA_KEY = 'vesselmind_learning_data';
const MAX_STORED_PAIRS = 100; // Limit storage to prevent bloat

/**
 * Check if learning mode is enabled
 * @returns {boolean}
 */
export const isLearningModeEnabled = () => {
  try {
    return localStorage.getItem(LEARNING_MODE_KEY) === 'true';
  } catch (error) {
    console.error('Failed to check learning mode:', error);
    return false;
  }
};

/**
 * Enable learning mode
 */
export const enableLearningMode = () => {
  try {
    localStorage.setItem(LEARNING_MODE_KEY, 'true');
    console.log('✅ Learning mode enabled - Q&A pairs will be stored locally');
  } catch (error) {
    console.error('Failed to enable learning mode:', error);
  }
};

/**
 * Disable learning mode
 */
export const disableLearningMode = () => {
  try {
    localStorage.setItem(LEARNING_MODE_KEY, 'false');
    console.log('✅ Learning mode disabled - VesselMind is now stateless');
  } catch (error) {
    console.error('Failed to disable learning mode:', error);
  }
};

/**
 * Get stored learning data
 * @returns {Array} Array of Q&A pairs
 */
const getLearningData = () => {
  try {
    const data = localStorage.getItem(LEARNING_DATA_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to load learning data:', error);
    return [];
  }
};

/**
 * Save learning data
 * @param {Array} data - Array of Q&A pairs
 */
const saveLearningData = (data) => {
  try {
    localStorage.setItem(LEARNING_DATA_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save learning data:', error);
  }
};

/**
 * Anonymize a question (remove potential PII)
 * @param {string} question - User's question
 * @returns {string} Anonymized question
 */
const anonymizeQuestion = (question) => {
  // Remove common PII patterns
  let anonymized = question
    .replace(/\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, '[NAME]') // Names
    .replace(/\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g, '[PHONE]') // Phone numbers
    .replace(/\b[\w.-]+@[\w.-]+\.\w+\b/g, '[EMAIL]') // Emails
    .replace(/\b\d{1,5}\s+\w+\s+(Street|St|Avenue|Ave|Road|Rd|Lane|Ln)\b/gi, '[ADDRESS]') // Addresses
    .replace(/\bmy company\b/gi, '[COMPANY]')
    .replace(/\bour site\b/gi, '[SITE]');
  
  return anonymized;
};

/**
 * Store a Q&A pair for learning (only if mode is enabled)
 * @param {string} question - User's question
 * @param {string} answer - VesselMind's answer
 * @param {Array} documentsSources - Documents that were used for context
 * @param {number} confidenceScore - Confidence score of the answer
 */
export const storeQAPair = (question, answer, documentSources = [], confidenceScore = 0) => {
  if (!isLearningModeEnabled()) {
    return; // Learning disabled - remain stateless
  }
  
  try {
    const learningData = getLearningData();
    
    // Anonymize the question
    const anonymizedQuestion = anonymizeQuestion(question);
    
    // Create Q&A pair
    const qaPair = {
      id: `qa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      question: anonymizedQuestion,
      questionKeywords: extractKeywords(anonymizedQuestion),
      documentSources: documentSources.map(doc => doc.title || doc),
      confidenceScore: confidenceScore,
      timestamp: new Date().toISOString(),
      useCount: 0 // Track how often this helps with retrieval
    };
    
    // Add to learning data (newest first)
    learningData.unshift(qaPair);
    
    // Limit storage to prevent bloat
    if (learningData.length > MAX_STORED_PAIRS) {
      learningData.pop(); // Remove oldest
    }
    
    saveLearningData(learningData);
    console.log('📚 Q&A pair stored for learning');
  } catch (error) {
    console.error('Failed to store Q&A pair:', error);
  }
};

/**
 * Extract keywords from a question
 * @param {string} question - Question text
 * @returns {Array} Array of keywords
 */
const extractKeywords = (question) => {
  // Remove common stop words and extract meaningful terms
  const stopWords = new Set([
    'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
    'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'should',
    'could', 'may', 'might', 'must', 'can', 'what', 'when', 'where', 'who',
    'how', 'why', 'which', 'this', 'that', 'these', 'those', 'i', 'you',
    'he', 'she', 'it', 'we', 'they', 'my', 'your', 'his', 'her', 'its',
    'our', 'their', 'me', 'him', 'them', 'us'
  ]);
  
  return question
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Remove punctuation
    .split(/\s+/)
    .filter(word => word.length > 3 && !stopWords.has(word))
    .slice(0, 10); // Top 10 keywords
};

/**
 * Get relevant past Q&A pairs for a new question
 * @param {string} question - New question
 * @returns {Array} Relevant past Q&A pairs
 */
export const getRelevantPastQA = (question) => {
  if (!isLearningModeEnabled()) {
    return []; // Learning disabled - no history
  }
  
  try {
    const learningData = getLearningData();
    const questionKeywords = extractKeywords(question);
    
    // Calculate similarity scores
    const scoredPairs = learningData.map(pair => {
      const matchingKeywords = questionKeywords.filter(keyword =>
        pair.questionKeywords.includes(keyword)
      ).length;
      
      const similarity = matchingKeywords / Math.max(questionKeywords.length, 1);
      
      return {
        ...pair,
        similarity: similarity
      };
    });
    
    // Return pairs with similarity > 0.3, sorted by similarity
    return scoredPairs
      .filter(pair => pair.similarity > 0.3)
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5); // Top 5 relevant pairs
  } catch (error) {
    console.error('Failed to get relevant past Q&A:', error);
    return [];
  }
};

/**
 * Improve document ranking using learning data
 * @param {Array} documents - Initial document ranking
 * @param {Array} relevantPastQA - Relevant past Q&A pairs
 * @returns {Array} Re-ranked documents
 */
export const improveDocumentRanking = (documents, relevantPastQA) => {
  if (!isLearningModeEnabled() || relevantPastQA.length === 0) {
    return documents; // No learning data - return as-is
  }
  
  try {
    // Count how often each document appeared in successful past answers
    const documentScores = new Map();
    
    relevantPastQA.forEach(qa => {
      qa.documentSources.forEach(docTitle => {
        const currentScore = documentScores.get(docTitle) || 0;
        // Weight by confidence score and similarity
        const boost = qa.confidenceScore * qa.similarity;
        documentScores.set(docTitle, currentScore + boost);
      });
    });
    
    // Re-rank documents based on learning data
    const rankedDocs = documents.map(doc => {
      const docTitle = doc.title || doc.documentTitle;
      const learningBoost = documentScores.get(docTitle) || 0;
      
      return {
        ...doc,
        relevanceScore: (doc.relevanceScore || 0) + learningBoost * 0.3, // 30% boost from learning
        learningBoost: learningBoost
      };
    });
    
    // Sort by new relevance score
    rankedDocs.sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    console.log('📊 Document ranking improved using learning data');
    return rankedDocs;
  } catch (error) {
    console.error('Failed to improve document ranking:', error);
    return documents;
  }
};

/**
 * Increment use count for a Q&A pair (tracks effectiveness)
 * @param {string} qaId - Q&A pair ID
 */
export const incrementQAUseCount = (qaId) => {
  if (!isLearningModeEnabled()) {
    return;
  }
  
  try {
    const learningData = getLearningData();
    const pair = learningData.find(qa => qa.id === qaId);
    
    if (pair) {
      pair.useCount = (pair.useCount || 0) + 1;
      saveLearningData(learningData);
    }
  } catch (error) {
    console.error('Failed to increment use count:', error);
  }
};

/**
 * Clear all learning data (useful for reset)
 */
export const clearLearningData = () => {
  try {
    localStorage.removeItem(LEARNING_DATA_KEY);
    console.log('✅ Learning data cleared');
  } catch (error) {
    console.error('Failed to clear learning data:', error);
  }
};

/**
 * Get learning statistics
 * @returns {Object} Statistics about stored learning data
 */
export const getLearningStats = () => {
  try {
    const learningData = getLearningData();
    const enabled = isLearningModeEnabled();
    
    return {
      enabled: enabled,
      totalPairs: learningData.length,
      maxPairs: MAX_STORED_PAIRS,
      oldestPair: learningData.length > 0 ? learningData[learningData.length - 1].timestamp : null,
      newestPair: learningData.length > 0 ? learningData[0].timestamp : null,
      mostUsedDocuments: getMostUsedDocuments(learningData)
    };
  } catch (error) {
    console.error('Failed to get learning stats:', error);
    return {
      enabled: false,
      totalPairs: 0,
      maxPairs: MAX_STORED_PAIRS,
      oldestPair: null,
      newestPair: null,
      mostUsedDocuments: []
    };
  }
};

/**
 * Get most frequently referenced documents from learning data
 * @param {Array} learningData - Learning data
 * @returns {Array} Top 5 most used documents
 */
const getMostUsedDocuments = (learningData) => {
  const docCounts = new Map();
  
  learningData.forEach(qa => {
    qa.documentSources.forEach(doc => {
      docCounts.set(doc, (docCounts.get(doc) || 0) + 1);
    });
  });
  
  return Array.from(docCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([doc, count]) => ({ document: doc, count }));
};

